import { getValidToken } from '../contexts/AuthUtils';
import sourceUrl from './Source.api';
import { clearAppStorage } from '../utils/storageUtils';

let isRefreshing = false;
let failedQueue = [];

const getAdminDelegateToken = () => localStorage.getItem('adminDelegateToken');

const buildHeaders = (options, token) => {
  const adminDelegateToken = getAdminDelegateToken();
  return {
    ...options.headers,
    Authorization: `Bearer ${token}`,
    ...(adminDelegateToken && { 'X-Admin-Token': `Bearer ${adminDelegateToken}` }),
  };
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const isLoginPage = () => {
  return window.location.pathname === '/login' || 
         window.location.pathname === '/signup' ||
         window.location.pathname === '/reset';
};

const handleAuthFailure = () => {
  if (isLoginPage()) return;
  clearAppStorage();
  window.location.href = '/login';
};

const isAuthError = async (response) => {
  if (response.status === 401 || response.status === 403) return true;
  
  if (response.status === 400) {
    try {
      const cloned = response.clone();
      const data = await cloned.json();
      const msg = (data.message || '').toLowerCase();
      const detail = (data.error?.message || '').toLowerCase();
      return msg.includes('expired token') || detail.includes('token has expired');
    } catch {
      return false;
    }
  }
  return false;
};

export const fetchWithAuth = async (endpoint, options = {}) => {
  const token = await getValidToken();

  if (!token) {
    const wasLoggedIn = localStorage.getItem('isLoggedIn');
    if (wasLoggedIn) {
      handleAuthFailure();
      throw new Error('Session expired - please login again');
    }
    throw new Error('Not authenticated');
  }

  const url = endpoint.startsWith('http') ? endpoint : `${sourceUrl}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: buildHeaders(options, token),
  });

  if (await isAuthError(response)) {
    console.warn('fetchWithAuth: Auth error detected for', endpoint, 'status:', response.status);
    
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(newToken => {
        return fetch(url, {
          ...options,
          headers: buildHeaders(options, newToken),
        });
      });
    }

    isRefreshing = true;

    try {
      const newToken = await getValidToken(true);
      if (newToken) {
        processQueue(null, newToken);
        console.log('fetchWithAuth: Retrying with refreshed token');
        const retryResponse = await fetch(url, {
          ...options,
          headers: buildHeaders(options, newToken),
        });
        if (await isAuthError(retryResponse)) {
          console.warn('fetchWithAuth: Retry also failed with auth error, redirecting to login');
          handleAuthFailure();
          throw new Error('Session expired - please login again');
        }
        return retryResponse;
      }
      throw new Error('Token refresh failed');
    } catch (refreshError) {
      processQueue(refreshError, null);
      console.warn('fetchWithAuth: Token refresh failed, redirecting to login');
      handleAuthFailure();
      throw refreshError;
    } finally {
      isRefreshing = false;
    }
  }

  return response;
};

export const fetchWithOptionalAuth = async (endpoint, options = {}) => {
  const isLoggedIn = localStorage.getItem("isLoggedIn");
  const token = isLoggedIn ? await getValidToken() : null;

  const url = endpoint.startsWith('http') ? endpoint : `${sourceUrl}${endpoint}`;
  const adminDelegateToken = getAdminDelegateToken();
  
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(token && adminDelegateToken && { 'X-Admin-Token': `Bearer ${adminDelegateToken}` }),
    },
  });

  if (token && await isAuthError(response)) {
    console.warn('fetchWithOptionalAuth: Auth error detected for', endpoint, 'status:', response.status);
    
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(newToken => {
        return fetch(url, {
          ...options,
          headers: buildHeaders(options, newToken),
        });
      });
    }

    isRefreshing = true;

    try {
      const newToken = await getValidToken(true);
      if (newToken) {
        processQueue(null, newToken);
        console.log('fetchWithOptionalAuth: Retrying with refreshed token');
        return fetch(url, {
          ...options,
          headers: buildHeaders(options, newToken),
        });
      }
      throw new Error('Token refresh failed');
    } catch (refreshError) {
      processQueue(refreshError, null);
      console.warn('fetchWithOptionalAuth: Token refresh failed, redirecting to login');
      handleAuthFailure();
      throw refreshError;
    } finally {
      isRefreshing = false;
    }
  }

  return response;
};

export default fetchWithAuth;
